import { ROLE } from 'services/assist-api/entities/role/roles';
import { MarkStatus } from 'services/assist-api/entities/test-instance-response-mark/types';
import type { TestInstanceResponseMarkerWithCustomerUser } from 'services/assist-api/entities/test-instance-response-marker/types';
import { hasRole } from './helpers';
import { markingAllocationRoles } from './marking-allocation.policy';
import { viewMarkingTaskRoles } from './marking-task.policy';
import type { AuthorisableUser } from './types';

// A constructed object representing a mark (that may not have been saved yet) being authorised.
export type AuthorisableMark = {
  id?: number; // Optional, as the mark may not have been created on the backend yet.
  activeMarkerId?: number; // Optional, as an active marker may not have been set yet.
  status?: MarkStatus;
  responseMarkers: TestInstanceResponseMarkerWithCustomerUser[];
};

// Helper functions (don't use these directly for authorising actions; use a policy action):
export function userIsAllocatedToResponse(user: AuthorisableUser, mark: AuthorisableMark): boolean {
  return mark.responseMarkers.find((m) => m.user.id === user.customerUser?.id) !== undefined;
}

export function userIsActiveMarker(user: AuthorisableUser, mark: AuthorisableMark): boolean {
  const currentResponseMarker = mark.responseMarkers.find(
    (m) => m.user.id === user.customerUser?.id
  );
  if (mark.id) {
    return mark.activeMarkerId === currentResponseMarker?.id;
  }
  // We can consider the active marker to be the current user if there is no mark.
  return currentResponseMarker !== undefined;
}

/**
 * Authorisation rules for the resource that the policy is named after.
 * Functions return true only if the user can perform the action.
 * Follows the policy pattern used in Gate (Laravel) and Pundit (Rails).
 */
export default class MarkPolicy {
  /**
   * Permits action if user can view the Mark Response page, but not necessarily the details of the
   * mark.
   */
  static viewPage(user: AuthorisableUser): boolean {
    if (!user.belongsToMarkingOrganisation) return false;
    return viewMarkingTaskRoles.some((role) => user.roles.includes(role));
  }

  /**
   * Permits action if user can view the Mark Response form and all details displayed on it.
   */
  static viewForm(user: AuthorisableUser, mark: AuthorisableMark): boolean {
    // Permits action if user has a marking allocation role, regardless of being allocated:
    if (hasRole(user, markingAllocationRoles)) return true;

    // Permits action if the user is both allocated to the response and is a marker:
    return hasRole(user, [ROLE.MARKER]) && userIsAllocatedToResponse(user, mark);
  }

  /**
   * Permits action if user can create or edit a Mark and any mark items, but not necessarily
   * override the status.
   */
  static edit(user: AuthorisableUser, mark: AuthorisableMark): boolean {
    // Forbids action if the user is not the active marker (unless the user has a marking allocation
    // role).
    if (!hasRole(user, markingAllocationRoles)) {
      if (!userIsActiveMarker(user, mark)) return false;
    }

    // Permits action if the mark has a review status and the user has a marking allocation role.
    // This allows admins to make changes to the mark for the duration of the review process.
    if (
      hasRole(user, markingAllocationRoles) &&
      mark.status !== undefined &&
      [MarkStatus.NeedsReview, MarkStatus.InReview, MarkStatus.FurtherEscalated].includes(
        mark.status
      )
    ) {
      return true;
    }

    // Permits action if the mark has an editing status:
    return (
      mark.status !== undefined &&
      [
        MarkStatus.Assigned,
        MarkStatus.InProgress,
        MarkStatus.ReturnedForMarking,
        MarkStatus.Reassigned
      ].includes(mark.status)
    );
  }

  /** Permits action if user can delete the mark. */
  static delete(user: AuthorisableUser, mark: AuthorisableMark): boolean {
    return hasRole(user, markingAllocationRoles) && mark.id !== undefined;
  }
}
